import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Alert } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { sendNewApplicationNotification } from '../services/emailService';
import { supabaseAnonOnly, toApplicationRow } from '../services/supabase';
import { getDiscordLoginUrl } from '../services/api';
import '../styles/Application.css';

const Application = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    fullName: '',
    age: '',
    characterName: '',
    discordId: '',
    experience: '',
    department: '',
    agreeToRules: false
  });
  const [discordFromOAuth, setDiscordFromOAuth] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  // Prefill Discord ID and username from OAuth callback (?discordId=...&discordUsername=...)
  useEffect(() => {
    const discordId = searchParams.get('discordId');
    const discordUsername = searchParams.get('discordUsername');
    const oauthError = searchParams.get('error');
    if (oauthError) {
      setError(oauthError === 'oauth_failed' ? 'Discord login failed. You can still enter your Discord details manually below.' : 'Something went wrong. You can enter your Discord details manually below.');
      setSearchParams({}, { replace: true });
      return;
    }
    if (discordId && discordUsername) {
      setFormData(prev => ({ ...prev, discordId, characterName: discordUsername }));
      setDiscordFromOAuth(true);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const departments = [
    'CIVILIAN',
    // 'LCFR (Fire Rescue)',
    'LCPD (Police Department)',
    'LCHP (Highway Patrol)',
    'LCSO (Sheriff Office)'
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.agreeToRules) {
      setError('You must agree to the rules to submit an application.');
      return;
    }

    if (!formData.fullName || !formData.age || !formData.experience || !formData.department) {
      setError('Please fill in all required fields.');
      return;
    }
    if (!formData.discordId || !formData.characterName) {
      setError('Please log in with Discord using the button above to continue.');
      return;
    }

    const submittedAt = new Date().toISOString();
    const application = {
      ...formData,
      id: '', // set after insert if using Supabase
      status: 'pending',
      submittedAt,
      approvedAt: null,
      deniedAt: null,
      adminNotes: ''
    };

    if (supabaseAnonOnly) {
      const row = toApplicationRow(application);
      const { error: insertError } = await supabaseAnonOnly
        .from('applications')
        .insert(row, { returning: 'minimal' });

      if (insertError) {
        console.error('Application submit error:', insertError);
        const msg = insertError.message || 'Unknown error';
        setError(
          process.env.NODE_ENV === 'development'
            ? `Failed to submit: ${msg}`
            : 'Failed to submit application. Please try again or contact staff.'
        );
        return;
      }
      application.id = 'submitted';
    } else {
      // Fallback when Supabase is not configured (dev only)
      application.id = Date.now().toString();
      const existingApplications = JSON.parse(localStorage.getItem('applications') || '[]');
      existingApplications.push(application);
      localStorage.setItem('applications', JSON.stringify(existingApplications));
    }

    try {
      await sendNewApplicationNotification(application);
    } catch (err) {
      console.error('Failed to send staff notification:', err);
    }

    setSubmitted(true);
    setError('');
  };

  if (submitted) {
    return (
      <div className="application-page">
        <Container className="mt-5">
          <Row className="justify-content-center">
            <Col md={8}>
              <div className="text-center">
                <h1 className="text-success mb-4">
                  <i className="fas fa-check-circle"></i> Application Submitted!
                </h1>
                <p className="lead">
                  Thank you for your application to Skyline RP. We will review your application 
                  and get back to you via Discord within 48-72 hours.
                </p>
                <Link to="/forum" className="btn btn-primary mt-3">
                  <i className="fas fa-arrow-left me-2"></i>
                  Back to Forum
                </Link>
              </div>
            </Col>
          </Row>
        </Container>
      </div>
    );
  }

  return (
    <div className="application-page">
      <Container className="mt-5">
        <Row className="justify-content-center">
          <Col md={10}>
            <div className="d-flex justify-content-between align-items-center mb-4">
              {/* <Link to="/forum" className="btn btn-outline-primary">
                <i className="fas fa-arrow-left me-2"></i>
                Back to Forum
              </Link> REMOVING SINCE IT IS REDUNDANT KEEPING DIV FOR SPACING*/}
            </div>
            
            <div className="application-header mb-4">
              <h1>Skyline RP Application</h1>
              <p className="lead">Please fill out the form below to apply for membership.</p>
            </div>

            {error && (
              <Alert variant="danger" dismissible onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <Form onSubmit={handleSubmit} className="application-form">
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Name *</Form.Label>
                    <Form.Control
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Age *</Form.Label>
                    <Form.Control
                      type="number"
                      name="age"
                      value={formData.age}
                      onChange={handleInputChange}
                      min="16"
                      required
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={12}>
                  <Form.Group className="mb-3">
                    <Form.Label>Discord account *</Form.Label>
                    {discordFromOAuth ? (
                      <div className="d-flex align-items-center flex-wrap gap-2">
                        <div className="d-flex align-items-center rounded border border-success bg-light px-3 py-2">
                          <i className="fab fa-discord text-primary me-2"></i>
                          <span><strong>{formData.characterName}</strong> <span className="text-muted">(ID: {formData.discordId})</span></span>
                        </div>
                        <Button
                          type="button"
                          variant="outline-secondary"
                          size="sm"
                          onClick={() => {
                            setFormData(prev => ({ ...prev, characterName: '', discordId: '' }));
                            setDiscordFromOAuth(false);
                          }}
                        >
                          Use different account
                        </Button>
                      </div>
                    ) : (
                      <div>
                        <Button
                          type="button"
                          variant="primary"
                          className="discord-oauth-btn"
                          onClick={() => { window.location.href = getDiscordLoginUrl(); }}
                        >
                          <i className="fab fa-discord me-2"></i>
                          Login with Discord
                        </Button>
                        <Form.Text className="d-block mt-1 text-muted">
                          You must log in with Discord to submit an application. We use this to verify your account and assign roles.
                        </Form.Text>
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Department You're Applying For*</Form.Label>
                <Form.Select
                  name="department"
                  value={formData.department}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select a department...</option>
                  {departments.map(dept => (
                    <option key={dept} value={dept}>{dept}</option>
                  ))}
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>FiveM Experience & Character Background *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={4}
                  name="experience"
                  value={formData.experience}
                  onChange={handleInputChange}
                  placeholder="Write a paragraph explaining your FiveM experience and your character's background story..."
                  required
                />
              </Form.Group>

              <div className="rules-section mb-4">
                <h4>Rules Agreement</h4>
                <p>
                  Please read our <a href="https://discord.com/channels/1380125200443641956/1380125203354484758" target="_blank" rel="noopener noreferrer">Discord Rules</a> before submitting your application.
                </p>
                <Form.Check
                  type="checkbox"
                  name="agreeToRules"
                  checked={formData.agreeToRules}
                  onChange={handleInputChange}
                  label="I have read and agree to follow the Skyline RP Discord rules and community guidelines."
                  required
                />
              </div>

              <div className="text-center">
                <Button type="submit" variant="success" size="lg">
                  <i className="fas fa-paper-plane me-2"></i>
                  Submit Application
                </Button>
              </div>
            </Form>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Application; 